/**
 * Suppliers and their price lists.
 *
 * `bank_account_number` is typed **optional** rather than as a string, and that
 * is not defensive typing — it is the contract. The server omits the field
 * entirely for anybody without `suppliers.view_banking_details`, so any code
 * here that assumed a string would be assuming a permission it does not have.
 */

import { api } from "@/api/client";

export interface Supplier {
  id: string;
  name: string;
  contact_name: string;
  phone: string;
  email: string;
  address: string;
  payment_terms_days: number;
  note: string;
  is_active: boolean;
  /** True whenever details are on file, whether or not this reader may see them. */
  has_banking_details: boolean;
  bank_name?: string;
  bank_account_number?: string;
  bank_branch_code?: string;
  created_at: string;
}

export interface SupplierProduct {
  id: string;
  supplier: string;
  product: string;
  product_name: string;
  supplier_code: string;
  units_per_pack: string;
  pack_cost: string;
  unit_cost: string;
  is_preferred: boolean;
  is_active: boolean;
}

interface Page<T> {
  count: number;
  next: string | null;
  results: T[];
}

function unwrap<T>(payload: Page<T> | T[]): T[] {
  return Array.isArray(payload) ? payload : payload.results;
}

export interface SupplierInput {
  name: string;
  contact_name?: string;
  phone?: string;
  email?: string;
  address?: string;
  payment_terms_days?: number;
  note?: string;
}

export const suppliersRepository = {
  list: async (activeOnly = false): Promise<Supplier[]> =>
    unwrap(
      await api.get<Page<Supplier> | Supplier[]>(`/suppliers/${activeOnly ? "?active=true" : ""}`),
    ),

  create: (body: SupplierInput & Partial<Record<"bank_name" | "bank_account_number" | "bank_branch_code", string>>) =>
    api.post<Supplier>("/suppliers/", body),

  update: (id: string, body: Partial<SupplierInput>) =>
    api.patch<Supplier>(`/suppliers/${id}/`, body),

  archive: (id: string) => api.delete<void>(`/suppliers/${id}/`),

  /** Separate from `update` because the server refuses banking fields there. */
  setBanking: (
    id: string,
    body: {
      bank_name?: string;
      bank_account_number?: string;
      bank_branch_code?: string;
      reason?: string;
    },
  ) => api.post<Supplier>(`/suppliers/${id}/banking/`, body),

  products: (id: string) => api.get<SupplierProduct[]>(`/suppliers/${id}/products/`),

  setProduct: (
    id: string,
    body: {
      product_id: string;
      units_per_pack: string;
      pack_cost: string;
      supplier_code?: string;
      is_preferred?: boolean;
    },
  ) => api.post<SupplierProduct>(`/suppliers/${id}/products/`, body),
};
